/**
 * The typed fetch client shared by the Admin, POS and Staff PWAs.
 *
 * Responsibilities (deliberately few, deliberately here rather than in each app):
 *   - attach the Bearer access token;
 *   - refresh ONCE on 401 and replay the request (single-flight, see below);
 *   - generate an `Idempotency-Key` for every mutation;
 *   - pass `x-action-reason` through for sensitive actions;
 *   - map the API error envelope to a typed `ApiError` carrying `.code`;
 *   - time out and abort;
 *   - short-circuit when the device is offline.
 *
 * What it deliberately does NOT do: caching, retry-on-500 loops, or optimistic
 * mutation. TanStack Query owns caching in the Admin app and the offline
 * outbox owns replay in POS/Staff.
 */
import type { ApiError} from './errors.js';
import { clientError, toApiError } from './errors.js';

export interface AuthTokens {
  readonly accessToken: string;
  readonly refreshToken: string;
  /** Epoch ms at which `accessToken` stops being accepted. */
  readonly expiresAt: number;
}

export interface ApiClientOptions {
  /** e.g. `/api` in dev (Vite proxy) or `https://api.example.com` in prod. */
  readonly baseUrl: string;
  /** Returns the tokens currently held, or null when signed out. */
  readonly getTokens: () => AuthTokens | null;
  /** Persists rotated tokens. Called after every successful refresh. */
  readonly setTokens: (tokens: AuthTokens | null) => void;
  /** Called when refresh fails — the app must send the user to /login. */
  readonly onUnauthenticated: () => void;
  /** Per-request timeout. Defaults to 20s; POS overrides it lower. */
  readonly timeoutMs?: number;
  /** `X-Client-Version`, useful when triaging a bug report. */
  readonly clientVersion?: string;
  /** Injected for tests. Defaults to `globalThis.fetch`. */
  readonly fetchImpl?: typeof fetch;
  /** Injected for tests. Defaults to `navigator.onLine`. */
  readonly isOnline?: () => boolean;
  /** Called for every completed request; used by the SyncStatusPill. */
  readonly onRequestSettled?: (info: RequestSettledInfo) => void;
}

export interface RequestSettledInfo {
  readonly method: HttpMethod;
  readonly path: string;
  readonly status: number;
  readonly durationMs: number;
  readonly error: ApiError | null;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  /** Query string values; `undefined`/`null` entries are dropped. */
  readonly query?: Readonly<Record<string, string | number | boolean | null | undefined>>;
  readonly body?: unknown;
  /**
   * Sent as `x-action-reason`. The API REJECTS sensitive actions without it
   * (see `permissionsRequiringReason()` in @sonauto/permissions), so the UI
   * must collect it in a ConfirmDialog before calling.
   */
  readonly reason?: string | null;
  /**
   * Explicit idempotency key. Omit and one is generated for every mutation —
   * pass your own when replaying a queued offline mutation, so the retry
   * carries the SAME key as the original attempt.
   */
  readonly idempotencyKey?: string | null;
  /** Set false for endpoints the API rejects an Idempotency-Key on. */
  readonly idempotent?: boolean;
  readonly timeoutMs?: number;
  readonly signal?: AbortSignal;
  /** Skip the Authorization header (login, refresh, /meta, /health). */
  readonly anonymous?: boolean;
  readonly headers?: Readonly<Record<string, string>>;
}

export interface ApiClient {
  request: <T>(method: HttpMethod, path: string, options?: RequestOptions) => Promise<T>;
  get: <T>(path: string, options?: RequestOptions) => Promise<T>;
  post: <T>(path: string, options?: RequestOptions) => Promise<T>;
  put: <T>(path: string, options?: RequestOptions) => Promise<T>;
  patch: <T>(path: string, options?: RequestOptions) => Promise<T>;
  del: <T>(path: string, options?: RequestOptions) => Promise<T>;
  /** Exposed so the login flow can seed tokens through the same guard. */
  readonly options: ApiClientOptions;
}

const DEFAULT_TIMEOUT_MS = 20_000;

/**
 * RFC-4122-ish v4. `crypto.randomUUID` exists in every browser this PWA
 * supports, but a plain HTTP LAN deployment has no secure context, so we fall
 * back to `getRandomValues` before ever touching `Math.random`.
 */
export function newIdempotencyKey(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c !== undefined && typeof c.randomUUID === 'function') return c.randomUUID();
  if (c !== undefined && typeof c.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

function buildUrl(baseUrl: string, path: string, query: RequestOptions['query']): string {
  const base = baseUrl.replace(/\/+$/, '');
  const rel = path.startsWith('/') ? path : `/${path}`;
  if (query === undefined) return `${base}${rel}`;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs === '' ? `${base}${rel}` : `${base}${rel}?${qs}`;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const online = options.isOnline ?? ((): boolean => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const defaultTimeout = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  /**
   * SINGLE-FLIGHT REFRESH.
   *
   * A screen typically fires 4–6 queries at mount. When the access token has
   * just expired all of them come back 401 within milliseconds of each other.
   * Refreshing per-response would send N refresh calls with the SAME refresh
   * token — and the API treats a reused refresh token as theft and burns the
   * whole token family (`REFRESH_TOKEN_REPLAY` in auth/session.ts), logging the
   * user out.
   *
   * So the FIRST 401 starts a refresh and stores its promise here; every other
   * concurrent 401 awaits that same promise and then replays with whatever
   * token it produced. One refresh, N replays.
   */
  let refreshInFlight: Promise<AuthTokens | null> | null = null;

  async function runRefresh(): Promise<AuthTokens | null> {
    const current = options.getTokens();
    if (current === null) return null;

    let response: Response;
    try {
      response = await doFetch(buildUrl(options.baseUrl, '/v1/auth/refresh', undefined), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });
    } catch {
      // A network failure is NOT an auth failure — keep the tokens so the user
      // is still signed in once connectivity returns.
      return null;
    }

    if (!response.ok) {
      options.setTokens(null);
      options.onUnauthenticated();
      return null;
    }

    const body: unknown = await response.json().catch(() => null);
    if (body === null || typeof body !== 'object') {
      options.setTokens(null);
      options.onUnauthenticated();
      return null;
    }
    const parsed = body as Partial<AuthTokens>;
    if (
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.refreshToken !== 'string' ||
      typeof parsed.expiresAt !== 'number'
    ) {
      options.setTokens(null);
      options.onUnauthenticated();
      return null;
    }
    const next: AuthTokens = {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      expiresAt: parsed.expiresAt,
    };
    options.setTokens(next);
    return next;
  }

  function refreshOnce(): Promise<AuthTokens | null> {
    refreshInFlight ??= runRefresh().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  async function send(
    method: HttpMethod,
    path: string,
    opts: RequestOptions,
    token: string | null,
  ): Promise<Response> {
    const headers = new Headers();
    headers.set('accept', 'application/json');
    if (opts.body !== undefined) headers.set('content-type', 'application/json');
    if (token !== null) headers.set('authorization', `Bearer ${token}`);
    if (options.clientVersion !== undefined) headers.set('x-client-version', options.clientVersion);

    // Sensitive actions are refused without this header. Trimmed because the
    // API validates a minimum length of 3 after trimming (`zReason`).
    const reason = opts.reason?.trim();
    if (reason !== undefined && reason !== '') headers.set('x-action-reason', reason);

    // Idempotency: every mutation carries a key unless the caller opted out.
    // GET is never idempotency-keyed — the API rejects the header there.
    const wantsKey = method !== 'GET' && (opts.idempotent ?? true);
    if (wantsKey) headers.set('idempotency-key', opts.idempotencyKey ?? newIdempotencyKey());

    for (const [k, v] of Object.entries(opts.headers ?? {})) headers.set(k, v);

    // Two abort sources: our own timeout and the caller's signal (React Query
    // aborts on unmount). `AbortSignal.any` is not available everywhere yet, so
    // they are bridged manually.
    const controller = new AbortController();
    const timeoutMs = opts.timeoutMs ?? defaultTimeout;
    const timer = setTimeout(() => controller.abort(new DOMException('timeout', 'TimeoutError')), timeoutMs);
    const onExternalAbort = (): void => controller.abort(opts.signal?.reason);
    if (opts.signal !== undefined) {
      if (opts.signal.aborted) controller.abort(opts.signal.reason);
      else opts.signal.addEventListener('abort', onExternalAbort, { once: true });
    }

    try {
      return await doFetch(buildUrl(options.baseUrl, path, opts.query), {
        method,
        headers,
        signal: controller.signal,
        ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
      });
    } finally {
      clearTimeout(timer);
      opts.signal?.removeEventListener('abort', onExternalAbort);
    }
  }

  async function request<T>(method: HttpMethod, path: string, opts: RequestOptions = {}): Promise<T> {
    const started = Date.now();
    const settle = (status: number, error: ApiError | null): void => {
      options.onRequestSettled?.({ method, path, status, durationMs: Date.now() - started, error });
    };

    // Offline detection. Failing fast here means the UI can show "ngoại tuyến"
    // instead of a 20-second spinner, and lets POS/Staff enqueue instead.
    if (!online()) {
      const err = clientError('OFFLINE');
      settle(0, err);
      throw err;
    }

    const anonymous = opts.anonymous ?? false;
    // Reuse the SAME idempotency key across the refresh replay: the replay is
    // the same logical attempt, so the server must dedupe it if the first try
    // actually landed before the 401 was written.
    const stickyKey =
      method !== 'GET' && (opts.idempotent ?? true) ? (opts.idempotencyKey ?? newIdempotencyKey()) : null;
    const effective: RequestOptions = stickyKey === null ? opts : { ...opts, idempotencyKey: stickyKey };

    let tokens = anonymous ? null : options.getTokens();

    let response: Response;
    try {
      response = await send(method, path, effective, tokens?.accessToken ?? null);
    } catch (e) {
      const err = toTransportError(e);
      settle(0, err);
      throw err;
    }

    if (response.status === 401 && !anonymous && tokens !== null) {
      const refreshed = await refreshOnce();
      if (refreshed === null) {
        const err = toApiError(401, await readBody(response));
        settle(401, err);
        throw err;
      }
      tokens = refreshed;
      try {
        response = await send(method, path, effective, tokens.accessToken);
      } catch (e) {
        const err = toTransportError(e);
        settle(0, err);
        throw err;
      }
      if (response.status === 401) {
        // A fresh token was still rejected: the session is genuinely gone.
        options.setTokens(null);
        options.onUnauthenticated();
      }
    }

    if (!response.ok) {
      const err = toApiError(response.status, await readBody(response));
      settle(response.status, err);
      throw err;
    }

    settle(response.status, null);
    if (response.status === 204) return undefined as T;
    const body = await readBody(response);
    return body as T;
  }

  return {
    request,
    get: <T>(path: string, o?: RequestOptions): Promise<T> => request<T>('GET', path, o),
    post: <T>(path: string, o?: RequestOptions): Promise<T> => request<T>('POST', path, o),
    put: <T>(path: string, o?: RequestOptions): Promise<T> => request<T>('PUT', path, o),
    patch: <T>(path: string, o?: RequestOptions): Promise<T> => request<T>('PATCH', path, o),
    del: <T>(path: string, o?: RequestOptions): Promise<T> => request<T>('DELETE', path, o),
    options,
  };
}

async function readBody(response: Response): Promise<unknown> {
  const type = response.headers.get('content-type') ?? '';
  if (!type.includes('json')) {
    const text = await response.text().catch(() => '');
    return text === '' ? null : { error: { code: 'UNKNOWN', message: text.slice(0, 400) } };
  }
  return response.json().catch(() => null);
}

function toTransportError(e: unknown): ApiError {
  if (e instanceof DOMException && e.name === 'TimeoutError') return clientError('TIMEOUT', 0, e);
  if (e instanceof DOMException && e.name === 'AbortError') return clientError('ABORTED', 0, e);
  if (typeof navigator !== 'undefined' && !navigator.onLine) return clientError('OFFLINE', 0, e);
  return clientError('NETWORK_ERROR', 0, e);
}
